<script setup lang="ts">
import {
  BadgeDollarSign,
  Check,
  CircleDollarSign,
  Clock3,
  Coins,
  LoaderCircle,
  PackagePlus,
  X,
} from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"

import { ApiError, apiRequest } from "../lib/api"
import { formatShanghaiTime } from "../lib/datetime"
import type { BillingOverview, PurchaseOrderItem } from "../lib/types"
import { useSessionStore } from "../stores/session"

const session = useSessionStore()
const overview = ref<BillingOverview | null>(null)
const loading = ref(true)
const submittingId = ref("")
const error = ref("")
const confirmation = ref<{
  action: "submit" | "cancel"
  kind: "subscription" | "topup"
  productId: string
  productName: string
  order?: PurchaseOrderItem
} | null>(null)

const canManage = computed(() => ["owner", "admin"].includes(session.workspace?.role ?? ""))
const pendingSubscription = computed(() =>
  overview.value?.orders.find(
    (order) => order.kind === "subscription" && order.status === "pending",
  ),
)
const pendingTopups = computed(
  () =>
    new Set(
      overview.value?.orders
        .filter((order) => order.kind === "topup" && order.status === "pending")
        .flatMap((order) => (order.topup_package_id ? [order.topup_package_id] : [])) ?? [],
    ),
)

onMounted(load)

async function load(): Promise<void> {
  const workspace = session.workspace
  if (!workspace) {
    loading.value = false
    error.value = "当前账户没有可用的 Workspace。"
    return
  }
  loading.value = true
  error.value = ""
  try {
    overview.value = await apiRequest<BillingOverview>(
      `/v1/workspaces/${workspace.id}/billing`,
    )
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载套餐信息失败"
  } finally {
    loading.value = false
  }
}

async function requestOrder(
  kind: "subscription" | "topup",
  productId: string,
): Promise<void> {
  const workspace = session.workspace
  if (!workspace || !canManage.value) return
  submittingId.value = productId
  error.value = ""
  try {
    await apiRequest(`/v1/workspaces/${workspace.id}/billing/orders`, {
      method: "POST",
      csrfToken: session.csrfToken,
      body: { kind, product_id: productId },
    })
    confirmation.value = null
    await load()
  } catch (cause) {
    error.value = cause instanceof ApiError ? cause.message : "提交申请失败"
  } finally {
    submittingId.value = ""
  }
}

async function cancelOrder(order: PurchaseOrderItem): Promise<void> {
  const workspace = session.workspace
  if (!workspace || order.status !== "pending") return
  submittingId.value = order.id
  try {
    await apiRequest(`/v1/workspaces/${workspace.id}/billing/orders/${order.id}`, {
      method: "DELETE",
      csrfToken: session.csrfToken,
    })
    confirmation.value = null
    await load()
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "取消申请失败"
  } finally {
    submittingId.value = ""
  }
}

function openOrderConfirmation(
  kind: "subscription" | "topup",
  productId: string,
  productName: string,
): void {
  confirmation.value = {
    action: "submit",
    kind,
    productId,
    productName,
  }
}

function openCancelConfirmation(order: PurchaseOrderItem): void {
  confirmation.value = {
    action: "cancel",
    kind: order.kind,
    productId: order.id,
    productName: order.product_name,
    order,
  }
}

function closeConfirmation(): void {
  if (!submittingId.value) confirmation.value = null
}

async function confirmBillingAction(): Promise<void> {
  const pending = confirmation.value
  if (!pending) return
  if (pending.action === "cancel" && pending.order) {
    await cancelOrder(pending.order)
    return
  }
  await requestOrder(pending.kind, pending.productId)
}

function formatMoney(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100)
}

function formatDate(value: string): string {
  return formatShanghaiTime(value, { dateStyle: "medium", timeStyle: "short" })
}

function orderStatus(status: PurchaseOrderItem["status"]): string {
  return {
    pending: "待管理员确认",
    fulfilled: "已生效",
    rejected: "已拒绝",
    canceled: "已取消",
    refunded: "已退款",
  }[status]
}
</script>

<template>
  <section data-screen-label="Billing">
    <div class="section-heading section-heading--page">
      <div class="page-heading">
        <span class="eyebrow">Plans & credits</span>
        <h1>套餐与额度</h1>
        <p>订阅套餐获得每期额度，或按需购买额外 Credits。当前采用申请制，支付接入后会自动结算。</p>
      </div>
      <div class="billing-balance">
        <span>额度余额</span>
        <strong>{{ overview?.credits.toLocaleString() ?? "—" }}</strong>
        <small>CREDITS</small>
      </div>
    </div>

    <div v-if="loading" class="billing-loading">
      <LoaderCircle class="spin" :size="21" />正在加载套餐
    </div>
    <template v-else-if="overview">
      <section class="current-plan">
        <div class="current-plan__icon"><BadgeDollarSign :size="22" /></div>
        <div>
          <span class="eyebrow">Current plan</span>
          <strong>{{ overview.subscription?.plan_name ?? "Free" }}</strong>
          <p v-if="overview.subscription">本期至 {{ formatDate(overview.subscription.current_period_end) }}</p>
          <p v-else>当前没有套餐，可继续使用赠送额度或提交套餐申请。</p>
        </div>
        <span v-if="pendingSubscription" class="status-pill"><i></i>套餐申请处理中</span>
        <span v-else-if="overview.subscription" class="status-pill status-pill--active"><i></i>ACTIVE</span>
      </section>

      <div v-if="!canManage" class="billing-notice">
        当前 Workspace 仅 owner 或 admin 可以提交套餐和充值申请。
      </div>
      <div v-else class="billing-notice">
        <Clock3 :size="15" />暂未接入在线支付。提交后由管理员核对并生效，不会立即扣款。
      </div>

      <section class="billing-section">
        <div class="billing-section__heading">
          <div><span class="eyebrow">Subscription</span><h2>月度套餐</h2></div>
          <p>套餐额度按周期独立发放，到期后不自动续期。</p>
        </div>
        <div class="plan-grid">
          <article v-for="plan in overview.plans" :key="plan.id" class="plan-card" :class="{ 'plan-card--current': overview.subscription?.plan_id === plan.id }">
            <header><div><span>{{ plan.name }}</span><p>{{ plan.description }}</p></div><CircleDollarSign :size="20" /></header>
            <div class="plan-price"><strong>{{ formatMoney(plan.monthly_price_minor, plan.currency) }}</strong><span>/ {{ plan.period_days }} 天</span></div>
            <div class="plan-credits">{{ plan.monthly_credits.toLocaleString() }} <span>Credits / 期</span></div>
            <ul><li v-for="feature in plan.features" :key="feature"><Check :size="13" />{{ feature }}</li></ul>
            <button
              class="primary-button"
              :disabled="!canManage || Boolean(pendingSubscription) || submittingId === plan.id || overview.subscription?.plan_id === plan.id"
              @click="openOrderConfirmation('subscription', plan.id, plan.name)"
            >
              <LoaderCircle v-if="submittingId === plan.id" class="spin" :size="15" />
              {{ overview.subscription?.plan_id === plan.id ? "当前套餐" : pendingSubscription ? "已有待处理申请" : "提交订阅申请" }}
            </button>
          </article>
        </div>
      </section>

      <section class="billing-section">
        <div class="billing-section__heading">
          <div><span class="eyebrow">Pay as you go</span><h2>按量充值</h2></div>
          <p>充值额度进入正式环境，可与套餐额度叠加使用。</p>
        </div>
        <div class="topup-grid">
          <article v-for="item in overview.topup_packages" :key="item.id" class="topup-card">
            <div class="topup-card__icon"><Coins :size="18" /></div>
            <div><strong>{{ item.credits.toLocaleString() }}</strong><span>Credits</span><small>{{ item.description }}</small></div>
            <div class="topup-card__action"><strong>{{ formatMoney(item.price_minor, item.currency) }}</strong><button class="secondary-button" :disabled="!canManage || pendingTopups.has(item.id) || submittingId === item.id" @click="openOrderConfirmation('topup', item.id, item.name)"><PackagePlus :size="14" />{{ pendingTopups.has(item.id) ? "待确认" : "申请充值" }}</button></div>
          </article>
        </div>
      </section>

      <section class="billing-section">
        <div class="billing-section__heading">
          <div><span class="eyebrow">Order history</span><h2>申请记录</h2></div>
        </div>
        <div class="table-shell billing-orders">
          <div v-if="overview.orders.length === 0" class="empty-state"><PackagePlus :size="22" /><strong>暂无申请记录</strong></div>
          <div v-else class="responsive-table">
            <table>
              <thead><tr><th>提交时间（上海）</th><th>类型</th><th>商品</th><th>金额 / 额度</th><th>状态</th><th></th></tr></thead>
              <tbody>
                <tr v-for="order in overview.orders" :key="order.id">
                  <td>{{ formatDate(order.created_at) }}</td>
                  <td>{{ order.kind === "subscription" ? "套餐" : "充值" }}</td>
                  <td><strong>{{ order.product_name }}</strong><small v-if="order.admin_note">{{ order.admin_note }}</small></td>
                  <td>{{ formatMoney(order.amount_minor, order.currency) }} · {{ order.credits.toLocaleString() }} Credits</td>
                  <td><span class="status-pill" :class="{ 'status-pill--active': order.status === 'fulfilled', 'status-pill--expired': order.status === 'rejected' }"><i></i>{{ orderStatus(order.status) }}</span></td>
                  <td><button v-if="order.status === 'pending' && canManage" class="billing-cancel" :disabled="submittingId === order.id" @click="openCancelConfirmation(order)"><X :size="14" />取消</button></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </template>
    <p v-if="error" class="form-error billing-error">{{ error }}</p>

    <div v-if="confirmation" class="modal-backdrop" @click.self="closeConfirmation">
      <section
        class="modal billing-confirm"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="`billing-confirm-title-${confirmation.action}`"
      >
        <div class="modal__heading">
          <div>
            <span class="eyebrow">{{ confirmation.action === "submit" ? "Confirm request" : "Cancel request" }}</span>
            <h2 :id="`billing-confirm-title-${confirmation.action}`">
              {{ confirmation.action === "submit" ? "确认提交申请" : "确认取消申请" }}
            </h2>
            <p>{{ confirmation.action === "submit" ? "请确认本次申请信息" : "取消后如有需要可重新提交" }}</p>
          </div>
          <button type="button" class="icon-button" aria-label="关闭" :disabled="Boolean(submittingId)" @click="closeConfirmation">
            <X :size="18" />
          </button>
        </div>

        <div class="billing-confirm__body">
          <div class="billing-confirm__product">
            <span class="billing-confirm__icon">
              <CircleDollarSign v-if="confirmation.kind === 'subscription'" :size="21" />
              <Coins v-else :size="20" />
            </span>
            <div>
              <small>{{ confirmation.kind === "subscription" ? "月度套餐" : "按量充值" }}</small>
              <strong>{{ confirmation.productName }}</strong>
            </div>
          </div>
          <p>
            <Clock3 :size="15" />
            <span v-if="confirmation.action === 'submit'">提交后不会立即扣款，管理员确认后额度才会到账。</span>
            <span v-else>该申请将从待处理队列中移除，管理员不会再进行审核。</span>
          </p>
        </div>

        <div class="modal__actions">
          <button type="button" class="secondary-button" :disabled="Boolean(submittingId)" @click="closeConfirmation">返回</button>
          <button
            type="button"
            class="primary-button"
            :class="{ 'billing-confirm__danger': confirmation.action === 'cancel' }"
            :disabled="Boolean(submittingId)"
            autofocus
            @click="confirmBillingAction"
          >
            <LoaderCircle v-if="submittingId" class="spin" :size="15" />
            {{ confirmation.action === "submit" ? "确认提交" : "确认取消" }}
          </button>
        </div>
      </section>
    </div>
  </section>
</template>

<style scoped>
.billing-balance { min-width: 170px; display: grid; gap: 4px; padding: 15px 18px; border: 1px solid var(--line-strong); background: var(--panel); }
.billing-balance span, .billing-balance small { color: var(--muted); font: 9px monospace; letter-spacing: .05em; text-transform: uppercase; }
.billing-balance strong { margin-top: 3px; font: 700 27px/1 monospace; }
.billing-loading { min-height: 320px; display: flex; align-items: center; justify-content: center; gap: 10px; color: var(--muted); }
.current-plan { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 16px; margin-top: 28px; padding: 18px; border: 1px solid var(--line); background: var(--panel); }
.current-plan__icon, .topup-card__icon { width: 42px; height: 42px; display: grid; place-items: center; color: var(--accent); background: var(--accent-soft); }
.current-plan strong { display: block; margin-top: 5px; font-size: 18px; }
.current-plan p { margin: 4px 0 0; color: var(--muted); font-size: 10px; }
.billing-notice { display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 11px 13px; border-left: 3px solid var(--success); color: var(--muted); background: var(--panel-muted); font-size: 11px; }
.billing-section { margin-top: 48px; }
.billing-section__heading { display: flex; align-items: end; justify-content: space-between; gap: 24px; margin-bottom: 16px; padding-bottom: 13px; border-bottom: 1px solid var(--line); }
.billing-section__heading h2 { margin: 5px 0 0; font-size: 20px; }
.billing-section__heading p { margin: 0; color: var(--muted); font-size: 11px; }
.plan-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.plan-card { min-width: 0; display: flex; flex-direction: column; padding: 21px; border: 1px solid var(--line); background: var(--panel); }
.plan-card--current { border-color: var(--success); box-shadow: inset 0 3px var(--success); }
.plan-card header { display: flex; justify-content: space-between; gap: 15px; }
.plan-card header > svg { color: var(--accent); }
.plan-card header span { font-size: 17px; font-weight: 720; }
.plan-card header p { min-height: 35px; margin: 5px 0 0; color: var(--muted); font-size: 10px; line-height: 1.6; }
.plan-price { display: flex; align-items: baseline; gap: 6px; margin-top: 22px; }
.plan-price strong { font: 700 27px/1 monospace; }
.plan-price span, .plan-credits span { color: var(--muted); font-size: 9px; }
.plan-credits { margin-top: 10px; font: 650 13px monospace; }
.plan-card ul { display: grid; gap: 9px; min-height: 90px; margin: 20px 0; padding: 16px 0 0; border-top: 1px solid var(--line); list-style: none; }
.plan-card li { display: flex; align-items: center; gap: 7px; color: var(--muted); font-size: 10px; }
.plan-card li svg { color: var(--success); }
.plan-card button { width: 100%; margin-top: auto; }
.topup-grid { display: grid; gap: 8px; }
.topup-card { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 15px; padding: 15px 17px; border: 1px solid var(--line); background: var(--panel); }
.topup-card > div:nth-child(2) { display: grid; grid-template-columns: auto 1fr; align-items: baseline; gap: 0 6px; }
.topup-card > div:nth-child(2) > strong { font: 700 19px monospace; }
.topup-card > div:nth-child(2) > span { color: var(--muted); font: 9px monospace; text-transform: uppercase; }
.topup-card > div:nth-child(2) > small { grid-column: 1 / -1; margin-top: 3px; color: var(--muted); font-size: 10px; }
.topup-card__action { display: flex; align-items: center; gap: 14px; }
.topup-card__action > strong { font: 650 15px monospace; }
.billing-orders { margin-top: 0; }
.billing-orders td small { display: block; margin-top: 4px; color: var(--accent); font-size: 9px; }
.billing-cancel { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; color: var(--muted); background: transparent; cursor: pointer; font-size: 10px; }
.billing-cancel:hover { color: var(--accent); }
.billing-error { margin-top: 16px; }
.billing-confirm { width: min(470px, 100%); }
.billing-confirm__body { display: grid; gap: 14px; }
.billing-confirm__product { display: flex; align-items: center; gap: 13px; padding: 15px; border: 1px solid var(--line); border-radius: 11px; background: var(--panel); }
.billing-confirm__icon { width: 40px; height: 40px; display: grid; flex: 0 0 auto; place-items: center; border-radius: 10px; color: var(--accent); background: var(--accent-soft); }
.billing-confirm__product > div { min-width: 0; display: grid; gap: 4px; }
.billing-confirm__product small { color: var(--muted); font: 600 9px monospace; letter-spacing: .06em; text-transform: uppercase; }
.billing-confirm__product strong { overflow: hidden; font-size: 15px; text-overflow: ellipsis; white-space: nowrap; }
.billing-confirm__body > p { display: flex; align-items: flex-start; gap: 9px; margin: 0; padding: 12px 13px; border-left: 3px solid var(--success); color: var(--muted); background: var(--panel-muted); font-size: 11px; line-height: 1.65; }
.billing-confirm__body > p svg { flex: 0 0 auto; margin-top: 1px; color: var(--success); }
.billing-confirm__danger { border-color: var(--accent); background: var(--accent); }

@media (max-width: 900px) {
  .plan-grid { grid-template-columns: 1fr; }
  .plan-card header p, .plan-card ul { min-height: 0; }
}
@media (max-width: 600px) {
  .billing-balance { width: 100%; }
  .current-plan { grid-template-columns: auto 1fr; }
  .current-plan > .status-pill { grid-column: 2; }
  .billing-section__heading { align-items: start; flex-direction: column; }
  .topup-card { grid-template-columns: auto 1fr; }
  .topup-card__action { grid-column: 1 / -1; justify-content: space-between; }
}
</style>
